// v10/src/components/admin/AdminUsers.js
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, Edit2, UserX, UserCheck,
  Search, X, Check, AlertCircle, Loader,
  Shield, GraduationCap, Plane, CheckCircle2,
  BadgeCheck, Calendar, ChevronDown, KeyRound, Eye, EyeOff,
  FileDown,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api, { userService, aircraftService, assignmentService, notificationService } from '../../services/apiService';
import ExportReportModal from '../common/ExportReportModal';
import { reportsService } from '../../services/reportsService';
import { hasFeature } from '../../utils/planFeatures';

// ── Google logo SVG ───────────────────────────────────────────────
const GoogleLogo = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// ── RoleBadge ─────────────────────────────────────────────────────
const RoleBadge = ({ role }) => {
  const { t } = useTranslation('admin');
  const CONFIG = {
    admin:      { label: t('role_admin_label'),      color: 'red',   Icon: Shield },
    instructor: { label: t('role_instructor_label'), color: 'blue',  Icon: GraduationCap },
    pilot:      { label: t('role_pilot_label'),      color: 'green', Icon: Plane },
  };
  const cfg = CONFIG[role] || CONFIG.pilot;
  const Icon = cfg.Icon;
  const colors = {
    red:   'bg-red-100 text-red-700 border-red-200',
    blue:  'bg-blue-100 text-blue-700 border-blue-200',
    green: 'bg-green-100 text-green-700 border-green-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${colors[cfg.color]}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
};

// ── Custom Select ─────────────────────────────────────────────────
const CustomSelect = ({ value, onChange, options, darkMode, minWidth = '160px' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  }, []);

  const selected = options.find(o => o.value === value) || options[0];

  return (
    <div ref={ref} style={{ position: 'relative', minWidth }}>
      <button type="button" onClick={() => setOpen(p => !p)}
        style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'8px', padding:'10px 14px', borderRadius:'12px', cursor:'pointer', border:`1px solid ${darkMode?'rgba(75,85,99,1)':'rgba(209,213,219,1)'}`, background:darkMode?'rgba(55,65,81,1)':'rgba(249,250,251,1)', color:darkMode?'white':'rgba(17,24,39,1)', fontSize:'14px', fontWeight:500, fontFamily:'inherit', outline:'none', transition:'border-color 0.15s, box-shadow 0.15s', boxShadow:open?`0 0 0 2px rgba(59,130,246,0.4)`:'none' }}>
        <span>{selected.label}</span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown style={{ width:'14px', height:'14px', opacity:0.6, flexShrink:0 }} />
        </motion.div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity:0, y:-6, scale:0.97 }} animate={{ opacity:1, y:0, scale:1 }} exit={{ opacity:0, y:-6, scale:0.97 }} transition={{ duration:0.13 }}
            style={{ position:'absolute', top:'calc(100% + 6px)', left:0, right:0, zIndex:200, borderRadius:'14px', overflow:'hidden', background:darkMode?'rgba(31,41,55,1)':'white', border:`1px solid ${darkMode?'rgba(75,85,99,1)':'rgba(229,231,235,1)'}`, boxShadow:'0 8px 28px rgba(0,0,0,0.15)', padding:'4px' }}>
            {options.map(opt => (
              <button key={opt.value} type="button" onClick={() => { onChange(opt.value); setOpen(false); }}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:'8px', padding:'9px 12px', borderRadius:'10px', border:'none', cursor:'pointer', background:opt.value===value?(darkMode?'rgba(59,130,246,0.2)':'rgba(59,130,246,0.08)'):'transparent', color:opt.value===value?(darkMode?'rgba(147,197,253,1)':'rgba(37,99,235,1)'):(darkMode?'rgba(209,213,219,1)':'rgba(55,65,81,1)'), fontSize:'13.5px', fontWeight:opt.value===value?600:400, fontFamily:'inherit', textAlign:'left', transition:'background 0.1s' }}
                onMouseEnter={e => { if (opt.value !== value) e.currentTarget.style.background = darkMode?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.04)'; }}
                onMouseLeave={e => { if (opt.value !== value) e.currentTarget.style.background = 'transparent'; }}>
                {opt.value === value ? <Check style={{ width:'13px', height:'13px', flexShrink:0 }} /> : <span style={{ width:'13px', flexShrink:0 }} />}
                {opt.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── License Modal ─────────────────────────────────────────────────
const LicenseModal = ({ user, onClose, darkMode }) => {
  const { t } = useTranslation('admin');
  const [form, setForm] = useState({
    license_number: user.license_number || '',
    license_expiry: user.license_expiry ? user.license_expiry.split('T')[0] : '',
    medical_expiry: user.medical_expiry ? user.medical_expiry.split('T')[0] : '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState('');

  const handleSave = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      await notificationService.updateLicenseFor(user.id, {
        license_number: form.license_number || null,
        license_expiry: form.license_expiry || null,
        medical_expiry: form.medical_expiry || null,
      });
      setSuccess(true);
      setTimeout(onClose, 1500);
    } catch (err) { setError(err.response?.data?.message || 'Error al guardar'); }
    finally { setLoading(false); }
  };

  const inputClass = `w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;

  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    return Math.round((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  };

  const ExpiryBadge = ({ dateStr }) => {
    const days = daysUntil(dateStr);
    if (days === null) return null;
    const color = days < 0 || days <= 7 ? 'text-red-500 bg-red-50' : days <= 30 ? 'text-amber-600 bg-amber-50' : 'text-green-600 bg-green-50';
    const label = days < 0 ? `Vencida hace ${Math.abs(days)}d` : days === 0 ? '¡Vence hoy!' : `Vence en ${days}d`;
    return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${color}`}>{label}</span>;
  };

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ scale:0.92, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.92, y:20 }}
        className={`w-full max-w-md rounded-3xl shadow-2xl overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}>
        <div className={`px-6 py-5 border-b ${darkMode?'border-gray-700':'border-gray-100'} flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <BadgeCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className={`font-bold text-base ${darkMode?'text-white':'text-gray-900'}`}>{t('license_title')}</h3>
              <p className={`text-xs ${darkMode?'text-gray-400':'text-gray-500'}`}>{user.name}</p>
            </div>
          </div>
          <button onClick={onClose} className={`p-2 rounded-xl ${darkMode?'hover:bg-gray-700 text-gray-400':'hover:bg-gray-100 text-gray-500'}`}><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
          <div>
            <label className={`block text-sm font-semibold mb-1.5 ${darkMode?'text-gray-300':'text-gray-700'}`}>Número de licencia ANAC</label>
            <input type="text" value={form.license_number} onChange={e => setForm(f => ({ ...f, license_number: e.target.value }))} className={inputClass} placeholder="Ej: 12345" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className={`text-sm font-semibold ${darkMode?'text-gray-300':'text-gray-700'}`}>Vencimiento de licencia</label>
              <ExpiryBadge dateStr={form.license_expiry} />
            </div>
            <input type="date" value={form.license_expiry} onChange={e => setForm(f => ({ ...f, license_expiry: e.target.value }))} className={inputClass} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className={`text-sm font-semibold ${darkMode?'text-gray-300':'text-gray-700'}`}>Vencimiento certificado médico</label>
              <ExpiryBadge dateStr={form.medical_expiry} />
            </div>
            <input type="date" value={form.medical_expiry} onChange={e => setForm(f => ({ ...f, medical_expiry: e.target.value }))} className={inputClass} />
          </div>
          <p className={`text-xs ${darkMode?'text-gray-500':'text-gray-400'}`}>
            💡 El sistema notificará al piloto automáticamente cuando falten 30, 15, 7 y 1 día para cada vencimiento.
          </p>
          {error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}
          {success && (
            <motion.div initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }}
              className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm font-semibold">
              <CheckCircle2 className="w-4 h-4" /> ¡Guardado exitosamente!
            </motion.div>
          )}
          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={loading}
              className="flex-1 py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {t('save_btn')}
            </button>
            <button type="button" onClick={onClose} className={`px-4 py-3 rounded-xl font-bold ${darkMode?'bg-gray-700 hover:bg-gray-600 text-white':'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>Cancelar</button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// ── Aircraft Assignment Modal ─────────────────────────────────────
const AssignmentModal = ({ user, onClose, darkMode }) => {
  const { t } = useTranslation('admin');
  const [allAircraft, setAllAircraft] = useState([]);
  const [assigned, setAssigned]       = useState(new Set());
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [acRes, assignRes] = await Promise.all([
          aircraftService.getAll({ is_active: 'true' }),
          assignmentService.getAll(),
        ]);
        setAllAircraft(acRes.data.data || []);
        const userAssigned = (assignRes.data.data || []).filter(a => a.user_id === user.id).map(a => a.aircraft_id);
        setAssigned(new Set(userAssigned));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [user.id]);

  const toggle = async (aircraftId) => {
    setSaving(aircraftId);
    try {
      if (assigned.has(aircraftId)) {
        await assignmentService.unassign(user.id, aircraftId);
        setAssigned(prev => { const s = new Set(prev); s.delete(aircraftId); return s; });
      } else {
        await assignmentService.assign(user.id, aircraftId);
        setAssigned(prev => new Set([...prev, aircraftId]));
      }
    } catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ scale:0.92, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.92, y:20 }}
        className={`w-full max-w-md rounded-3xl shadow-2xl overflow-hidden ${darkMode?'bg-gray-800':'bg-white'}`}
        onClick={e => e.stopPropagation()}>
        <div className={`px-6 py-5 border-b ${darkMode?'border-gray-700':'border-gray-100'} flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
              <Plane className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className={`font-bold text-base ${darkMode?'text-white':'text-gray-900'}`}>{t('assign_aircraft')}</h3>
              <p className={`text-xs ${darkMode?'text-gray-400':'text-gray-500'}`}>{user.name}</p>
            </div>
          </div>
          <button onClick={onClose} className={`p-2 rounded-xl ${darkMode?'hover:bg-gray-700 text-gray-400':'hover:bg-gray-100 text-gray-500'}`}><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-4 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8"><Loader className="w-6 h-6 animate-spin text-blue-500" /></div>
          ) : allAircraft.length === 0 ? (
            <div className="text-center py-8">
              <Plane className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className={`text-sm ${darkMode?'text-gray-500':'text-gray-400'}`}>No hay aeronaves activas</p>
            </div>
          ) : (
            <div className="space-y-2">
              {allAircraft.map(ac => {
                const isAssigned = assigned.has(ac.id);
                const isSaving   = saving === ac.id;
                return (
                  <motion.button key={ac.id} onClick={() => toggle(ac.id)} disabled={!!saving} whileTap={{ scale:0.97 }}
                    className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 transition-all text-left ${isAssigned ? (darkMode?'bg-blue-900/30 border-blue-700':'bg-blue-50 border-blue-300') : (darkMode?'bg-gray-700/40 border-gray-700 hover:border-gray-600':'bg-gray-50 border-gray-200 hover:border-gray-300')}`}>
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isAssigned?'bg-blue-500':darkMode?'bg-gray-600':'bg-gray-200'}`}>
                      {isSaving ? <Loader className="w-4 h-4 text-white animate-spin" />
                        : isAssigned ? <CheckCircle2 className="w-4 h-4 text-white" />
                        : <Plane className={`w-4 h-4 ${darkMode?'text-gray-300':'text-gray-500'}`} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold text-sm ${darkMode?'text-white':'text-gray-900'}`}>{ac.manufacturer} {ac.model_name}</p>
                      {ac.registration && <p className={`text-xs font-mono ${darkMode?'text-gray-400':'text-gray-500'}`}>{ac.registration}</p>}
                    </div>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${isAssigned?'bg-blue-500 text-white':darkMode?'bg-gray-600 text-gray-400':'bg-gray-200 text-gray-500'}`}>
                      {isAssigned ? 'Asignada' : 'Sin asignar'}
                    </span>
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>
        <div className={`px-6 py-4 border-t ${darkMode?'border-gray-700':'border-gray-100'} flex items-center justify-between`}>
          <p className={`text-xs ${darkMode?'text-gray-500':'text-gray-400'}`}>
            {assigned.size} aeronave{assigned.size !== 1 ? 's' : ''} asignada{assigned.size !== 1 ? 's' : ''}
          </p>
          <button onClick={onClose} className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-violet-600 text-white rounded-xl font-bold text-sm">Listo</button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── User Modal ────────────────────────────────────────────────────
const UserModal = ({ user, currentUserRole, onSave, onClose, darkMode, quickLogEnabled }) => {
  const { t } = useTranslation('admin');
  const isEdit = !!user?.id;
  const isGoogleAccount = isEdit && !!user?.is_google_account;

  const roleOptions = [
    { value: 'pilot',      label: t('role_pilot_label') },
    { value: 'instructor', label: t('role_instructor_label') },
    { value: 'admin',      label: t('role_admin_label') },
  ];

  // Parte 2 — defaults: si no vienen del backend, asumimos checklist=true / quick_log=false
  const initialChecklist = user?.can_use_checklist !== false;
  const initialQuickLog  = user?.can_use_quick_log === true;

  const [form, setForm]               = useState({
    name:user?.name||'',
    email:user?.email||'',
    role:user?.role||'pilot',
    password:'',
    is_google_account:false,
    can_use_checklist: initialChecklist,
    can_use_quick_log: initialQuickLog,
  });
  const [showPwField, setShowPwField] = useState(false);
  const [showPw, setShowPw]           = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error,   setError]           = useState('');

  // Toggles para roles que vuelan (piloto/instructor) y si la org tiene quick_log_enabled prendido.
  const showPermissionToggles = quickLogEnabled && ['pilot', 'instructor'].includes(form.role);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    // Defensa UI: mínimo uno habilitado
    if (showPermissionToggles && !form.can_use_checklist && !form.can_use_quick_log) {
      setError('El usuario debe tener al menos una modalidad habilitada');
      setLoading(false);
      return;
    }
    try { await onSave(isEdit ? user.id : null, form, isEdit); onClose(); }
    catch (err) { setError(err.response?.data?.message || 'Error al guardar'); }
    finally { setLoading(false); }
  };

  const inputClass = `w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${darkMode?'bg-gray-700 border-gray-600 text-white':'bg-white border-gray-300 text-gray-900'}`;

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ scale:0.9, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.9, y:20 }}
        className={`w-full max-w-md rounded-3xl shadow-2xl p-6 ${darkMode?'bg-gray-800':'bg-white'}`}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className={`text-xl font-bold ${darkMode?'text-white':'text-gray-900'}`}>
              {isEdit ? t('edit_user_title') : t('new_user_title')}
            </h3>
            {isGoogleAccount && (
              <span className="inline-flex items-center gap-1.5 mt-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">
                <GoogleLogo size={11} /> Cuenta Google
              </span>
            )}
          </div>
          <button onClick={onClose} className={`p-2 rounded-xl ${darkMode?'hover:bg-gray-700':'hover:bg-gray-100'}`}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Nombre</label>
            <input type="text" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={inputClass} placeholder="Juan Pérez" autoComplete="off" />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Email</label>
            <input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={inputClass} placeholder="juan@aeroclub.com" autoComplete="off" />
          </div>
          {(currentUserRole === 'admin' || currentUserRole === 'superadmin') && (
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${darkMode?'text-gray-300':'text-gray-700'}`}>Rol</label>
              <CustomSelect value={form.role} onChange={val => setForm(f => ({ ...f, role: val }))} options={roleOptions} darkMode={darkMode} minWidth="100%" />
            </div>
          )}

          {showPermissionToggles && (
            <div className={`p-3 rounded-2xl border ${darkMode?'border-gray-700 bg-gray-700/30':'border-gray-200 bg-gray-50'}`}>
              <p className={`text-sm font-semibold mb-2 ${darkMode?'text-gray-200':'text-gray-700'}`}>Modalidades habilitadas</p>

              {/* Checklist asistido */}
              {(() => {
                const canTurnOff = form.can_use_quick_log === true;
                return (
                  <label
                    className="flex items-center gap-3 py-1.5 cursor-pointer select-none"
                    onClick={() => {
                      if (form.can_use_checklist && !canTurnOff) return; // mínimo uno
                      setForm(f => ({ ...f, can_use_checklist: !f.can_use_checklist }));
                    }}
                    title={form.can_use_checklist && !canTurnOff ? 'Debe quedar al menos una modalidad habilitada' : ''}
                  >
                    <div style={{ width:40, height:22, borderRadius:11, position:'relative', cursor:'pointer', background:form.can_use_checklist?'linear-gradient(135deg,#3b82f6,#6366f1)':darkMode?'rgba(255,255,255,0.1)':'#e2e8f0', transition:'background 0.2s', opacity:(form.can_use_checklist && !canTurnOff)?0.7:1 }}>
                      <div style={{ width:18, height:18, borderRadius:'50%', background:'white', position:'absolute', top:2, left:form.can_use_checklist?20:2, transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${darkMode?'text-gray-200':'text-gray-800'}`}>Checklist asistido</p>
                      <p className={`text-xs ${darkMode?'text-gray-400':'text-gray-500'}`}>Permite volar con el chequeo prevuelo guiado.</p>
                    </div>
                  </label>
                );
              })()}

              {/* Registro rápido */}
              {(() => {
                const canTurnOff = form.can_use_checklist === true;
                return (
                  <label
                    className="flex items-center gap-3 py-1.5 cursor-pointer select-none"
                    onClick={() => {
                      if (form.can_use_quick_log && !canTurnOff) return; // mínimo uno
                      setForm(f => ({ ...f, can_use_quick_log: !f.can_use_quick_log }));
                    }}
                    title={form.can_use_quick_log && !canTurnOff ? 'Debe quedar al menos una modalidad habilitada' : ''}
                  >
                    <div style={{ width:40, height:22, borderRadius:11, position:'relative', cursor:'pointer', background:form.can_use_quick_log?'linear-gradient(135deg,#f59e0b,#f97316)':darkMode?'rgba(255,255,255,0.1)':'#e2e8f0', transition:'background 0.2s', opacity:(form.can_use_quick_log && !canTurnOff)?0.7:1 }}>
                      <div style={{ width:18, height:18, borderRadius:'50%', background:'white', position:'absolute', top:2, left:form.can_use_quick_log?20:2, transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${darkMode?'text-gray-200':'text-gray-800'}`}>Registro rápido</p>
                      <p className={`text-xs ${darkMode?'text-gray-400':'text-gray-500'}`}>Registrar vuelos sin checklist obligatorio.</p>
                    </div>
                  </label>
                );
              })()}

              <p className={`text-xs mt-2 ${darkMode?'text-gray-500':'text-gray-400'}`}>El usuario debe tener al menos una modalidad activa.</p>
            </div>
          )}

          {!isEdit ? (
            <>
              <div>
                <label className="flex items-center gap-2 cursor-pointer select-none"
                  onClick={() => setForm(f => ({ ...f, is_google_account: !f.is_google_account, password: '' }))}>
                  <div style={{ width:40, height:22, borderRadius:11, position:'relative', cursor:'pointer', background:form.is_google_account?'linear-gradient(135deg,#4285f4,#34a853)':darkMode?'rgba(255,255,255,0.1)':'#e2e8f0', transition:'background 0.2s' }}>
                    <div style={{ width:18, height:18, borderRadius:'50%', background:'white', position:'absolute', top:2, left:form.is_google_account?20:2, transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                  <GoogleLogo size={14} />
                  <span className={`text-sm font-medium ${darkMode?'text-gray-300':'text-gray-700'}`}>Es cuenta Google</span>
                </label>
                {form.is_google_account && <p className={`text-xs mt-2 ${darkMode?'text-blue-400':'text-blue-600'}`}>El usuario iniciará sesión con Google. No se requiere contraseña.</p>}
              </div>
              {!form.is_google_account && (
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Contraseña</label>
                  <div className="relative">
                    <input type={showPw?'text':'password'} required value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className={inputClass} style={{ paddingRight:44 }} placeholder="Mín. 8 caracteres, 1 mayúscula, 1 número" minLength={8} />
                    <button type="button" onClick={() => setShowPw(p => !p)} className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-colors ${darkMode?'text-gray-400 hover:text-white':'text-gray-400 hover:text-gray-700'}`}>
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : isGoogleAccount ? (
            <div className={`flex items-start gap-3 p-3 rounded-xl border ${darkMode?'bg-blue-900/20 border-blue-800 text-blue-300':'bg-blue-50 border-blue-200 text-blue-700'}`}>
              <GoogleLogo size={16} />
              <p className="text-xs font-medium">Este usuario inicia sesión con Google. La contraseña es gestionada por Google y no puede modificarse desde aquí.</p>
            </div>
          ) : (
            <div>
              <button type="button" onClick={() => { setShowPwField(p => !p); setForm(f => ({ ...f, password: '' })); }}
                className={`flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-xl transition-all ${showPwField ? (darkMode?'bg-blue-900/40 text-blue-300':'bg-blue-50 text-blue-600') : (darkMode?'text-gray-400 hover:bg-gray-700':'text-gray-500 hover:bg-gray-100')}`}>
                <KeyRound className="w-4 h-4" />
                {showPwField ? t('cancel_password_change') : t('change_password')}
              </button>
              <AnimatePresence>
                {showPwField && (
                  <motion.div initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }} exit={{ opacity:0, height:0 }} transition={{ duration:0.2 }} className="overflow-hidden mt-3">
                    <div className="relative">
                      <input type={showPw?'text':'password'} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className={inputClass} style={{ paddingRight:44 }} placeholder="Nueva contraseña (mín. 8 caracteres)" minLength={8} />
                      <button type="button" onClick={() => setShowPw(p => !p)} className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-colors ${darkMode?'text-gray-400 hover:text-white':'text-gray-400 hover:text-gray-700'}`}>
                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className={`text-xs mt-1.5 ${darkMode?'text-gray-500':'text-gray-400'}`}>Dejá vacío para no cambiar la contraseña actual.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={loading} className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold flex items-center justify-center gap-2">
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {isEdit ? t('save_changes_btn') : t('create_user_btn')}
            </button>
            <button type="button" onClick={onClose} className={`px-4 py-3 rounded-xl font-bold ${darkMode?'bg-gray-700 hover:bg-gray-600 text-white':'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>Cancelar</button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// ── Confirm Modal ────────────────────────────────────────────────
const ConfirmModal = ({ mode, user, onConfirm, onClose, darkMode, loading }) => {
  const { t } = useTranslation('admin');
  const [typed, setTyped] = React.useState('');
  const isDelete  = mode === 'delete';
  const confirmed = isDelete ? typed.trim().toLowerCase() === user.name.trim().toLowerCase() : true;

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ scale:0.9, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.9, y:20 }}
        className={`w-full max-w-md rounded-3xl shadow-2xl overflow-hidden ${darkMode?'bg-gray-800':'bg-white'}`}
        onClick={e => e.stopPropagation()}>

        <div style={{ height:'4px', background:isDelete?'linear-gradient(90deg,#ef4444,#dc2626)':'linear-gradient(90deg,#f59e0b,#d97706)' }} />

        <div className="p-6">
          <div style={{ width:'56px', height:'56px', borderRadius:'18px', margin:'0 auto 16px', background:isDelete?'rgba(239,68,68,0.1)':'rgba(245,158,11,0.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'28px' }}>
            {isDelete ? '🗑️' : '⚠️'}
          </div>

          <h3 className={`text-center text-lg font-black mb-2 ${darkMode?'text-white':'text-gray-900'}`}>
            {isDelete ? t('delete_user_title') : user.is_active ? t('deactivate_user') : t('activate_user')}
          </h3>

          <p className={`text-center text-sm mb-4 ${darkMode?'text-gray-400':'text-gray-600'}`}>
            {isDelete ? (
              <><span>Estás por eliminar permanentemente a </span><strong className={darkMode?'text-white':'text-gray-900'}>{user.name}</strong><span>. Esta acción </span><strong className="text-red-500">no se puede deshacer</strong>.</>
            ) : user.is_active ? (
              <><span>¿Desactivar a </span><strong className={darkMode?'text-white':'text-gray-900'}>{user.name}</strong><span>? No podrá ingresar a la app. Podés reactivarlo después.</span></>
            ) : (
              <><span>¿Reactivar a </span><strong className={darkMode?'text-white':'text-gray-900'}>{user.name}</strong><span>? Volverá a poder ingresar a la app.</span></>
            )}
          </p>

          <div className={`flex items-center gap-3 p-3 rounded-2xl mb-4 ${darkMode?'bg-gray-700':'bg-gray-50'}`}>
            <div style={{ width:'36px', height:'36px', borderRadius:'50%', flexShrink:0, background:user.role==='admin'?'#e0e7ff':user.role==='instructor'?'#dbeafe':'#dcfce7', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:'14px', color:user.role==='admin'?'#4f46e5':user.role==='instructor'?'#2563eb':'#16a34a' }}>
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className={`font-bold text-sm ${darkMode?'text-white':'text-gray-900'}`}>{user.name}</p>
              <p className={`text-xs ${darkMode?'text-gray-400':'text-gray-500'}`}>{user.email}</p>
            </div>
          </div>

          {isDelete && (
            <div className="mb-4">
              <p className={`text-xs font-semibold mb-1.5 ${darkMode?'text-gray-400':'text-gray-600'}`}>
                Escribí <strong className={darkMode?'text-white':'text-gray-900'}>{user.name}</strong> para confirmar:
              </p>
              <input type="text" value={typed} onChange={e => setTyped(e.target.value)} placeholder={user.name}
                className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 ${confirmed&&typed?'border-red-400 focus:ring-red-400':darkMode?'border-gray-600 focus:ring-red-500 bg-gray-700 text-white':'border-gray-300 focus:ring-red-400'}`} />
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={onClose} className={`flex-1 py-3 rounded-xl font-bold text-sm ${darkMode?'bg-gray-700 hover:bg-gray-600 text-white':'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>Cancelar</button>
            <motion.button onClick={onConfirm} disabled={loading || !confirmed}
              whileHover={(!loading && confirmed) ? { scale:1.02 } : {}} whileTap={(!loading && confirmed) ? { scale:0.97 } : {}}
              style={{ flex:1, padding:'12px', borderRadius:'12px', border:'none', cursor:(loading||!confirmed)?'not-allowed':'pointer', fontWeight:800, fontSize:'14px', color:'white', opacity:(loading||!confirmed)?0.5:1, background:isDelete?'linear-gradient(135deg,#ef4444,#dc2626)':user.is_active?'linear-gradient(135deg,#f59e0b,#d97706)':'linear-gradient(135deg,#22c55e,#16a34a)', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px', fontFamily:'inherit' }}>
              {loading
                ? <><Loader className="w-4 h-4 animate-spin" /> Procesando...</>
                : isDelete
                  ? <><span>🗑️</span> Eliminar definitivamente</>
                  : user.is_active ? <><UserX className="w-4 h-4" /> Desactivar</> : <><UserCheck className="w-4 h-4" /> Activar</>
              }
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Main Component ────────────────────────────────────────────────
const AdminUsers = ({ currentUser, darkMode }) => {
  const { t } = useTranslation('admin');
  // Parte 2 — Capa 1 (org-level) gatea Capa 2 (UI por piloto).
  const quickLogEnabled = hasFeature(currentUser, 'quick_log_enabled');

  const FILTER_OPTIONS = [
    { value: '',           label: t('all_roles') },
    { value: 'admin',      label: t('role_admin_label') },
    { value: 'instructor', label: t('role_instructor_label') },
    { value: 'pilot',      label: t('role_pilot_label') },
  ];

  const [users, setUsers]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [roleFilter, setRoleFilter]     = useState('');
  const [modalUser, setModalUser]       = useState(null);
  const [showModal, setShowModal]       = useState(false);
  const [assignUser, setAssignUser]     = useState(null);
  const [licenseUser, setLicenseUser]   = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [error, setError]               = useState('');
  // Modal de logbook (descarga PDF/Excel del libro de vuelos del piloto seleccionado).
  // Solo aplica a users con role='pilot'.
  const [logbookModal, setLogbookModal] = useState(null);  // { userId, userName } o null

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await userService.getAll(roleFilter ? { role: roleFilter } : {});
      setUsers(res.data.data || []);
    } catch { setError('Error al cargar usuarios'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadUsers(); }, [roleFilter]);

  const handleSave = async (id, form, isEdit) => {
    // Parte 2 + Bloque B — mandamos los permisos al backend cuando la feature está activa
    // y el rol es pilot o instructor. Para otros roles dejamos los defaults de DB.
    const includePerms = quickLogEnabled && ['pilot', 'instructor'].includes(form.role);
    if (isEdit) {
      const payload = { name: form.name, email: form.email, role: form.role };
      if (form.password && form.password.trim().length >= 8) payload.password = form.password;
      if (includePerms) {
        payload.can_use_checklist = !!form.can_use_checklist;
        payload.can_use_quick_log = !!form.can_use_quick_log;
      }
      await userService.update(id, payload);
    } else {
      const payload = { name: form.name, email: form.email, role: form.role };
      if (form.is_google_account) payload.is_google_account = true;
      else payload.password = form.password;
      if (includePerms) {
        payload.can_use_checklist = !!form.can_use_checklist;
        payload.can_use_quick_log = !!form.can_use_quick_log;
      }
      await api.post('/api/users', payload);
    }
    await loadUsers();
  };

  const handleToggleActive    = (user) => setConfirmModal({ mode: 'deactivate', user });
  const handleDeletePermanent = (user) => setConfirmModal({ mode: 'delete', user });

  const handleConfirmAction = async () => {
    if (!confirmModal) return;
    const { mode, user } = confirmModal;
    setConfirmLoading(true);
    try {
      if (mode === 'deactivate') {
        user.is_active
          ? await userService.delete(user.id)
          : await userService.update(user.id, { is_active: true });
      } else {
        await api.delete(`/api/users/${user.id}/permanent`);
      }
      setConfirmModal(null);
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || t('error_action'));
      setConfirmModal(null);
    } finally { setConfirmLoading(false); }
  };

  const getExpiryStatus = (user) => {
    if (user.role !== 'pilot') return null;
    const daysUntil = (dateStr) => {
      if (!dateStr) return null;
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const expiry = new Date(dateStr); expiry.setHours(0, 0, 0, 0);
      return Math.round((expiry - today) / (1000 * 60 * 60 * 24));
    };
    const licDays = daysUntil(user.license_expiry);
    const medDays = daysUntil(user.medical_expiry);
    const issues = [];
    if (licDays !== null && licDays < 0)             issues.push({ expired: true,  doc: 'Licencia', days: licDays });
    if (medDays !== null && medDays < 0)             issues.push({ expired: true,  doc: 'CMA',      days: medDays });
    if (licDays !== null && licDays >= 0 && licDays <= 30) issues.push({ expired: false, doc: 'Licencia', days: licDays });
    if (medDays !== null && medDays >= 0 && medDays <= 30) issues.push({ expired: false, doc: 'CMA',      days: medDays });
    if (issues.length === 0) return null;
    issues.sort((a, b) => { if (a.expired !== b.expired) return a.expired ? -1 : 1; return a.days - b.days; });
    const worst = issues[0];
    if (worst.expired) {
      const absDays = Math.abs(worst.days);
      const label = issues.filter(i => i.expired).length > 1
        ? 'Licencia y CMA vencidos'
        : `${worst.doc} vencido${absDays === 1 ? ' (ayer)' : ` hace ${absDays}d`}`;
      return { expired: true, label, color: 'red', issues };
    }
    const docsProx = issues.filter(i => !i.expired).map(i => i.doc);
    const label = docsProx.length > 1
      ? 'Licencia y CMA por vencer'
      : `${worst.doc} vence en ${worst.days === 0 ? '¡hoy!' : `${worst.days}d`}`;
    return { expired: false, label, color: 'amber', issues };
  };

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const cardClass = `rounded-2xl border p-4 transition-all ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className={`text-2xl font-bold ${darkMode?'text-white':'text-gray-900'}`}>{t('users_title')}</h2>
          <p className={`text-sm mt-1 ${darkMode?'text-gray-400':'text-gray-600'}`}>{t('users_desc', { count: users.length })}</p>
        </div>
        <motion.button onClick={() => { setModalUser(null); setShowModal(true); }}
          className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold shadow-lg"
          whileHover={{ scale:1.03 }} whileTap={{ scale:0.97 }}>
          <Plus className="w-5 h-5" /> {t('new_user')}
        </motion.button>
      </div>

      {/* Search + filter */}
      <div className={`${cardClass} flex flex-col sm:flex-row gap-3`}>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder={t('users_search')} value={search} onChange={e => setSearch(e.target.value)}
            className={`w-full pl-10 pr-4 py-2.5 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode?'bg-gray-700 border-gray-600 text-white':'bg-gray-50 border-gray-200 text-gray-900'}`} />
        </div>
        <CustomSelect value={roleFilter} onChange={setRoleFilter} options={FILTER_OPTIONS} darkMode={darkMode} minWidth="160px" />
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader className="w-8 h-8 animate-spin text-blue-500" /></div>
      ) : filtered.length === 0 ? (
        <div className={`${cardClass} text-center py-16`}>
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className={`font-medium ${darkMode?'text-gray-400':'text-gray-600'}`}>{t('user_no_results')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {filtered.map((user, i) => (
              <motion.div key={user.id} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
                exit={{ opacity:0, y:-10 }} transition={{ delay: i * 0.04 }}
                className={`${cardClass} flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 hover:shadow-md`}>

                {/* Sub-fila avatar + info (en mobile ocupa el top, en sm+ es inline) */}
                <div className="flex items-center gap-4 flex-1 min-w-0 w-full">

                {/* Avatar */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 ${
                  user.role==='admin'?'bg-indigo-100 text-indigo-600':user.role==='instructor'?'bg-blue-100 text-blue-600':'bg-green-100 text-green-600'
                }`}>
                  {user.name.charAt(0).toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`font-semibold ${darkMode?'text-white':'text-gray-900'} ${!user.is_active?'line-through opacity-50':''}`}>{user.name}</p>
                    <RoleBadge role={user.role} />
                    {user.is_google_account && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">
                        <GoogleLogo size={10} /> Google
                      </span>
                    )}
                    {!user.is_active && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full border">{t('user_inactive_badge')}</span>}
                    {(() => {
                      const status = getExpiryStatus(user);
                      if (!status) return null;
                      return status.expired
                        ? <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full border border-red-200 font-semibold">🚨 {status.label}</span>
                        : <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200 font-semibold">⚠ {status.label}</span>;
                    })()}
                  </div>
                  <p className={`text-sm truncate ${darkMode?'text-gray-400':'text-gray-500'}`}>{user.email}</p>
                  {(user.license_expiry || user.medical_expiry) && (
                    <div className={`flex flex-wrap gap-x-3 gap-y-0.5 text-xs mt-0.5 ${darkMode?'text-gray-500':'text-gray-400'}`}>
                      {user.license_expiry && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Lic: {new Date(user.license_expiry).toLocaleDateString('es-AR')}</span>}
                      {user.medical_expiry && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Méd: {new Date(user.medical_expiry).toLocaleDateString('es-AR')}</span>}
                    </div>
                  )}
                </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end w-full sm:w-auto sm:justify-start sm:flex-nowrap">
                  {user.role === 'pilot' && (
                    <motion.button onClick={() => setLicenseUser(user)}
                      className={`p-2 rounded-xl transition-all relative ${darkMode?'hover:bg-gray-700 text-gray-400 hover:text-amber-400':'hover:bg-amber-50 text-gray-500 hover:text-amber-600'}`}
                      whileHover={{ scale:1.1 }} whileTap={{ scale:0.9 }} title={t('license_title')}>
                      <BadgeCheck className="w-4 h-4" />
                      {(() => {
                        const s = getExpiryStatus(user);
                        if (!s) return null;
                        return <span className={`absolute top-1 right-1 w-2 h-2 rounded-full ${s.expired?'bg-red-500':'bg-amber-400'}`} />;
                      })()}
                    </motion.button>
                  )}
                  {user.role === 'pilot' && (
                    <motion.button onClick={() => setAssignUser(user)}
                      className={`p-2 rounded-xl transition-all ${darkMode?'hover:bg-gray-700 text-gray-400 hover:text-violet-400':'hover:bg-violet-50 text-gray-500 hover:text-violet-600'}`}
                      whileHover={{ scale:1.1 }} whileTap={{ scale:0.9 }} title={t('assign_aircraft')}>
                      <Plane className="w-4 h-4" />
                    </motion.button>
                  )}
                  {user.role === 'pilot' && (
                    <motion.button onClick={() => setLogbookModal({ userId: user.id, userName: user.name })}
                      className={`p-2 rounded-xl transition-all ${darkMode?'hover:bg-gray-700 text-gray-400 hover:text-sky-400':'hover:bg-sky-50 text-gray-500 hover:text-sky-600'}`}
                      whileHover={{ scale:1.1 }} whileTap={{ scale:0.9 }} title={t('report_button_logbook')}>
                      <FileDown className="w-4 h-4" />
                    </motion.button>
                  )}
                  <motion.button onClick={() => { setModalUser(user); setShowModal(true); }}
                    className={`p-2 rounded-xl transition-all ${darkMode?'hover:bg-gray-700 text-gray-400 hover:text-blue-400':'hover:bg-blue-50 text-gray-500 hover:text-blue-600'}`}
                    whileHover={{ scale:1.1 }} whileTap={{ scale:0.9 }} title="Editar">
                    <Edit2 className="w-4 h-4" />
                  </motion.button>
                  {(currentUser.role === 'admin' || currentUser.role === 'superadmin') && currentUser.id !== user.id && (
                    currentUser.role === 'admin' && user.role === 'admin' ? (
                      <span className={`text-xs font-semibold px-2 py-1 rounded-lg ${darkMode?'text-gray-500 bg-gray-700/50':'text-gray-400 bg-gray-100'}`}>
                        Contactá al SuperAdmin
                      </span>
                    ) : (
                      <>
                        <motion.button onClick={() => handleToggleActive(user)}
                          className={`p-2 rounded-xl transition-all ${user.is_active ? (darkMode?'hover:bg-gray-700 text-gray-400 hover:text-red-400':'hover:bg-red-50 text-gray-500 hover:text-red-600') : (darkMode?'hover:bg-gray-700 text-gray-400 hover:text-green-400':'hover:bg-green-50 text-gray-500 hover:text-green-600')}`}
                          whileHover={{ scale:1.1 }} whileTap={{ scale:0.9 }} title={user.is_active?t('deactivate_user'):t('activate_user')}>
                          {user.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </motion.button>
                        <motion.button onClick={() => handleDeletePermanent(user)}
                          className={`p-2 rounded-xl transition-all ${darkMode?'hover:bg-gray-700 text-gray-400 hover:text-red-500':'hover:bg-red-50 text-gray-400 hover:text-red-600'}`}
                          whileHover={{ scale:1.1 }} whileTap={{ scale:0.9 }} title={t('delete_user_title')}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                        </motion.button>
                      </>
                    )
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {confirmModal && (
          <ConfirmModal mode={confirmModal.mode} user={confirmModal.user} onConfirm={handleConfirmAction}
            onClose={() => setConfirmModal(null)} darkMode={darkMode} loading={confirmLoading} />
        )}
        {showModal && (
          <UserModal user={modalUser} currentUserRole={currentUser.role} onSave={handleSave}
            onClose={() => { setShowModal(false); setModalUser(null); }} darkMode={darkMode}
            quickLogEnabled={quickLogEnabled} />
        )}
        {assignUser && <AssignmentModal user={assignUser} onClose={() => setAssignUser(null)} darkMode={darkMode} />}
        {licenseUser && <LicenseModal user={licenseUser} onClose={() => { setLicenseUser(null); loadUsers(); }} darkMode={darkMode} />}
        {logbookModal && (
          <ExportReportModal
            isOpen
            title={t('report_modal_title_logbook', { name: logbookModal.userName })}
            formats={['pdf', 'xlsx']}
            periodOptions={{ monthPicker: true, rangePicker: true, allPicker: true }}
            onExport={(params) => reportsService.logbook({ ...params, user_id: logbookModal.userId })}
            onClose={() => setLogbookModal(null)}
            darkMode={darkMode}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminUsers;
